import { Inject, Injectable, NgZone } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

// RxJs
import { Subject, throwError, BehaviorSubject, Observable, of, from, switchMap } from 'rxjs';
import {tap, catchError, filter, takeUntil, mergeMap, map} from 'rxjs/operators';

// ENVs
import { authConfig, environment as env } from 'environments/environment';

// Services
import { LoadingService } from '@app/core/loading/loading.service';
import { UserService } from '@app/core/user/user.service';

import { ToastrService } from 'ngx-toastr';
import { KeycloakService } from './keycloak/keycloak.service';
import { ContextService } from '../../context/context.service';



@Injectable({
  providedIn: 'root'
})
export class SecurityService {

  // public userSession: AccountInfo;
  public userSession: KeycloakProfile;
  public showIframeLogout: boolean;
  
  private logoutTimeout: NodeJS.Timeout;
  private logoutTimestamp: number;
  private thirtyMinutes: number;
  public readonly authenticationStatus$ = new BehaviorSubject<any>(EventType.INITIALIZE_START);
  private readonly _destroying$ = new Subject<void>();

  private $token: string;
  private $refreshToken: string | undefined;
  private $refreshInterval: NodeJS.Timeout;

  constructor(
    private readonly ngZone: NgZone,
    private readonly router: Router,
    private readonly httpClient: HttpClient,
    private readonly httpBackend: HttpBackend,
    private readonly domSanitizer: DomSanitizer,
    private readonly activatedRoute: ActivatedRoute,
    private readonly userService: UserService,
    private readonly toastrService: ToastrService,
    private readonly loadingService: LoadingService,
    private contextService: ContextService,
    private keycloakService: KeycloakService
  ) {
    this.loadingService.show('Iniciando sesión');
    this.thirtyMinutes = authConfig.logoutTimeoutMinutes * 60 * 1000;
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    this.fetchPGSUser();
  }

  
  public logIn(): void {
    this.keycloakService.init();
  }


  public restartLogoutTimeout(): void {
    this.logoutTimeout && clearTimeout(this.logoutTimeout);
    this.initializeSessionCountdown();
  }

  
  public logOut(): void {
    this.cleanStorage();
    this.authenticationStatus$.next(EventType.LOGOUT_START);
    
    this.keycloakService.logout().then(() => {
      this.authenticationStatus$.next(EventType.LOGOUT_SUCCESS);
      this.finishSubscriptions();
    }).catch(err => {
      this.authenticationStatus$.next(EventType.LOGOUT_FAILURE);
      console.error({ err });
    });
  }


  private finishSubscriptions(): void {
    this.$stopTokenRefresh();
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }


  private fetchPGSUser(): void {
    this.authenticationStatus$.next(EventType.LOGIN_START);
    console.log({ token: this.token });
    this.generateAuth(this.keycloakService.getToken()).pipe(
      switchMap(res => {
        console.log({ res });
        this.token = res?.token;
        this.$refreshToken = res?.refresh_token;
        return this.userService.fetchUser(this.keycloakService.getUserInfo()?.username).pipe(
          tap((res) => {
            console.log({ res });
            this.loadingService.hide();
            this.initializeSessionCountdown();
            this.authenticationStatus$.next(EventType.LOGIN_SUCCESS);
          }),
          catchError(err => {
            localStorage.setItem('error-gateway', 'error: ' + JSON.stringify(err));
            if (err?.error) {
              switch (err?.status) {
                case 403:
                  this.toastrService.error('Error 403-1 - Usuario no registrado.');
                  break;
                case 409:
                  this.toastrService.error('Error 409-1 - Usuario inactivo.');
                  break;
                default:
                  this.toastrService.error('Ha ocurrido un problema durante el inicio de sesión.');
              }
            }
            this.showIframeLogout = true;
            this.cleanStorage();
            this.authenticationStatus$.next(EventType.LOGIN_FAILURE);
            this.loadingService.hide();
            return throwError(err);
          })
        );
      }), 
    ).subscribe();
  }


  private initializeSessionCountdown(): void {
    this.logoutTimestamp = performance.now() + this.thirtyMinutes;
    
    this.ngZone.runOutsideAngular(() => this.logoutTimeout = setTimeout(() => this.ngZone.run(() => this.logOut()), this.thirtyMinutes));
  }


  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      if (performance.now() >= this.logoutTimestamp) {
        clearTimeout(this.logoutTimeout);
        this.logOut();
      }
    }
  }

  
  public isAuthenticated(next: ActivatedRouteSnapshot, state: RouterStateSnapshot, typeCanActivate: string): Observable<boolean | UrlTree> {
    console.log({ keycloak: this.keycloakService.getUserInfo(), token: this.keycloakService.getToken() });
    return of(!!this.keycloakService.getUserInfo())
  }

  
  private cleanStorage(): void {
    localStorage.clear();
    sessionStorage.clear();
    this.userService.deleteUser();
  }
  
  
  private generateAuth(token: string): Observable<any> {
    const headers = new HttpHeaders({
      'x-api-key': '3pQY3Ve9qsA6CHTwFZZxLF0xiKIgL7Vj',
      'x-tenant-id': this.contextService?.country || 'CL',
      'Content-Type': 'application/json'
    });
    
    return (new HttpClient(this.httpBackend)).post(`${ env.baseUrl }${ env.authGenerateToken }`, { token }, { headers }).pipe(
      tap(() => this.$startTokenRefresh()),
    );
  }
  
  
  private $refreshAuth(refreshToken: string): Observable<any> {
    const headers = new HttpHeaders({
      'x-api-key': '3pQY3Ve9qsA6CHTwFZZxLF0xiKIgL7Vj',
      'x-tenant-id': this.contextService?.country || 'CL',
      'Content-Type': 'application/json'
    });
    
    return (new HttpClient(this.httpBackend)).post(`${ env.baseUrl }${ env.authRefreshToken }`, { refresh_token: refreshToken }, { headers });
  }
  
  
  private $startTokenRefresh(): void {
    // Se ejecuta cada 45 minutos
    this.$refreshInterval = setInterval(() => {
      this.$refreshAuth(this.$refreshToken).pipe(
        catchError(err => throwError(err))
      ).subscribe((res) => {
        console.log('Token renovado:', res);
        this.token = res?.token;
        this.$refreshToken = res?.refresh_token;
      });
    }, 45 * 60 * 1000); // (2700000 ms)
  }
  
  
  private $stopTokenRefresh(): void {
    if (this.$refreshInterval) {
      clearInterval(this.$refreshInterval);
    }
  }
  
  
  public get token(): string {
    return this.$token;
  }
  
  private set token(newToken: string) {
    this.$token = newToken;
  }
}


export enum EventType {
  LOGOUT_START = 'LOGOUT_START',
  LOGOUT_SUCCESS = 'LOGOUT_SUCCESS',
  LOGOUT_FAILURE = 'LOGOUT_FAILURE',
  INITIALIZE_START = 'INITIALIZE_START',
  LOGIN_START = 'LOGIN_START',
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
  LOGIN_FAILURE = 'LOGIN_FAILURE'
}


export interface KeycloakProfile {
  id?: string;               // ID del usuario (propiedad única)
  username?: string;         // Nombre de usuario
  email?: string;            // Correo electrónico del usuario
  firstName?: string;        // Primer nombre del usuario
  lastName?: string;         // Apellido del usuario
  enabled?: boolean;         // Indica si la cuenta está habilitada
  emailVerified?: boolean;   // Indica si el correo está verificado
  totp?: boolean;            // Indica si está habilitada la autenticación multifactor
  createdTimestamp?: number; // Fecha de creación de la cuenta en formato timestamp
  attributes?: Record<string, string[]>; // Atributos personalizados del usuario
}
